import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';
import { AppButton } from './AppButton';
import { AppInput } from './AppInput';
import { AppScreen } from './AppScreen';
import { spacing } from '../constants/spacing';
import { normalizeApiError } from '../api/apiError';
import { useAppColors } from '../context/PreferencesContext';
import {
  getHrContact,
  quickRegisterVisitor,
  recognizeVisitor,
} from '../services/visitorService';
import type { AttendanceCapture } from '../types/faceAttendance';
import type { HrContact, QuickVisitorResult, VisitorRecognition } from '../types/visitor';
import { formatTime } from '../utils/dateTime';
import { visitorErrorMessage } from '../utils/visitorErrors';
import {
  EMPTY_QUICK_VISITOR_FORM,
  validateQuickVisitorForm,
  type QuickVisitorFormValues,
  type VisitorFormErrors,
} from '../utils/visitorForm';

/** How long the HR contact stays on screen before the kiosk returns home. */
export const HR_CONTACT_DISPLAY_MS = 3000;

/** A walked-away prompt or half-filled form returns home (and clears the details). */
export const VISITOR_INACTIVITY_MS = 60000;

type Step =
  | 'recognizing'
  | 'visitDone'
  | 'recognitionFailed'
  | 'prompt'
  | 'hrContact'
  | 'form'
  | 'welcome';

/**
 * Recognition outcomes that mean "not a registered visitor here", so the
 * person is asked whether they are a visitor. A 404 without one of these
 * codes is a server that has no /visitors/recognize yet.
 */
const NOT_A_VISITOR_CODES = new Set([
  'VISITOR_FACE_NOT_MATCHED',
  'VISITOR_NOT_FOUND',
  'VISITOR_FACE_PROVIDER_UNSUPPORTED',
  'VISITOR_MODULE_NOT_INSTALLED',
]);

function isNotAVisitor(error: unknown): boolean {
  const apiError = normalizeApiError(error);
  return (
    (apiError.code != null && NOT_A_VISITOR_CODES.has(apiError.code)) ||
    apiError.statusCode === 404
  );
}

type Props = {
  /** Back to the kiosk landing screen. */
  onExit: () => void;
  /** How long the welcome / thank-you message stays before returning home. */
  welcomeDisplayMs: number;
  /**
   * The face captured at Mark Attendance. It is first matched against
   * registered visitors, and enrolled with a new registration. The kiosk
   * screen owns it and frees it when this flow closes.
   */
  faceImage?: AttendanceCapture | null;
};

type HrContactState =
  | { status: 'loading' }
  | { status: 'loaded'; contact: HrContact }
  | { status: 'failed' };

/**
 * Mark Attendance fallback when the captured face matched no employee. A
 * registered visitor is recognised from the same capture and checked out (or
 * in); anyone else is asked "Are you a visitor?" → register as a visitor (the
 * capture is enrolled as their face), see the HR contact, or cancel.
 * No employee attendance is involved.
 */
export function KioskVisitorFlow({ onExit, welcomeDisplayMs, faceImage = null }: Props) {
  const palette = useAppColors();
  const [step, setStep] = useState<Step>(faceImage ? 'recognizing' : 'prompt');
  const [recognition, setRecognition] = useState<VisitorRecognition | null>(null);
  const [recognitionError, setRecognitionError] = useState('');
  const [registration, setRegistration] = useState<QuickVisitorResult | null>(null);
  const recognitionStarted = useRef(false);
  const [hrContact, setHrContact] = useState<HrContactState>({ status: 'loading' });
  const [values, setValues] = useState<QuickVisitorFormValues>(EMPTY_QUICK_VISITOR_FORM);
  const [errors, setErrors] = useState<VisitorFormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const exitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mounted = useRef(true);

  const themed = useMemo(
    () => ({
      title: { fontSize: 24, lineHeight: 32, fontWeight: '700' as const, color: palette.textPrimary, textAlign: 'center' as const },
      body: { fontSize: 16, lineHeight: 24, color: palette.textPrimary, textAlign: 'center' as const },
      muted: { fontSize: 14, lineHeight: 20, color: palette.textMuted, textAlign: 'center' as const },
      label: { fontSize: 14, lineHeight: 20, color: palette.textMuted },
      value: { fontSize: 20, lineHeight: 28, fontWeight: '600' as const, color: palette.textPrimary },
      error: { fontSize: 14, lineHeight: 20, color: palette.error },
      card: { backgroundColor: palette.surface, borderColor: palette.border },
      banner: { backgroundColor: palette.surface, borderColor: palette.error },
    }),
    [palette],
  );

  const clearExitTimer = useCallback(() => {
    if (exitTimer.current) {
      clearTimeout(exitTimer.current);
      exitTimer.current = null;
    }
  }, []);

  const exitAfter = useCallback(
    (ms: number) => {
      clearExitTimer();
      exitTimer.current = setTimeout(() => {
        exitTimer.current = null;
        onExit();
      }, ms);
    },
    [clearExitTimer, onExit],
  );

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      clearExitTimer();
    };
  }, [clearExitTimer]);

  // Separate from exitTimer so it can never cancel the HR / welcome return.
  useEffect(() => {
    if ((step !== 'prompt' && step !== 'form') || submitting) return;
    const idle = setTimeout(onExit, VISITOR_INACTIVITY_MS);
    return () => clearTimeout(idle);
  }, [onExit, step, submitting, values]);

  const exitNow = useCallback(() => {
    clearExitTimer();
    onExit();
  }, [clearExitTimer, onExit]);

  useEffect(() => {
    if (!faceImage || recognitionStarted.current) return;
    recognitionStarted.current = true;
    recognizeVisitor(faceImage)
      .then(result => {
        if (!mounted.current) return;
        setRecognition(result);
        setStep('visitDone');
        exitAfter(welcomeDisplayMs);
      })
      .catch(error => {
        if (!mounted.current) return;
        if (isNotAVisitor(error)) {
          setStep('prompt');
          return;
        }
        setRecognitionError(
          visitorErrorMessage(error, 'We could not check your visitor registration.'),
        );
        setStep('recognitionFailed');
        exitAfter(HR_CONTACT_DISPLAY_MS);
      });
  }, [exitAfter, faceImage, welcomeDisplayMs]);

  const showHrContact = useCallback(async () => {
    setStep('hrContact');
    setHrContact({ status: 'loading' });
    try {
      const contact = await getHrContact();
      if (!mounted.current) return;
      setHrContact({ status: 'loaded', contact });
    } catch {
      if (!mounted.current) return;
      setHrContact({ status: 'failed' });
    }
    // The 3 seconds count from when the contact (or the fallback message) is shown.
    exitAfter(HR_CONTACT_DISPLAY_MS);
  }, [exitAfter]);

  const setField = useCallback((key: keyof QuickVisitorFormValues, value: string) => {
    setValues(current => ({ ...current, [key]: value }));
    setErrors(current => ({ ...current, [key]: undefined }));
    setSubmitError(null);
  }, []);

  const submit = useCallback(async () => {
    if (submitting) return;
    const found = validateQuickVisitorForm(values);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = await quickRegisterVisitor(values, faceImage);
      if (!mounted.current) return;
      setRegistration(result);
      setWelcomeMessage(result.welcomeMessage);
      setStep('welcome');
      exitAfter(welcomeDisplayMs);
    } catch (error) {
      if (!mounted.current) return;
      setSubmitError(visitorErrorMessage(error, 'Unable to register you as a visitor.'));
    } finally {
      if (mounted.current) setSubmitting(false);
    }
  }, [exitAfter, faceImage, submitting, values, welcomeDisplayMs]);

  if (step === 'recognizing') {
    return (
      <AppScreen contentStyle={styles.center}>
        <View style={[styles.card, themed.card]}>
          <ActivityIndicator size="large" color={palette.info} />
          <Text
            style={themed.body}
            accessibilityRole="progressbar"
            accessibilityLabel="Checking visitor registration"
          >
            Checking visitor registration…
          </Text>
        </View>
      </AppScreen>
    );
  }

  if (step === 'visitDone' && recognition) {
    const checkedOut = recognition.action === 'CHECK_OUT';
    const time = checkedOut ? recognition.visit.checkOutAt : recognition.visit.checkInAt;
    return (
      <AppScreen contentStyle={styles.center}>
        <View
          style={[styles.card, themed.card]}
          accessibilityRole="summary"
          accessibilityLabel={checkedOut ? 'Visitor checked out' : 'Visitor checked in'}
        >
          <View style={[styles.icon, { backgroundColor: palette.success }]}>
            <Text style={[styles.iconMark, { color: palette.onSuccess }]}>✓</Text>
          </View>
          <Text style={themed.title}>{recognition.message}</Text>
          <View style={styles.contactRow}>
            <Text style={themed.label}>{checkedOut ? 'Check Out Time' : 'Check In Time'}</Text>
            <Text style={themed.value}>{formatTime(time)}</Text>
          </View>
          <Text style={themed.muted}>Returning to home shortly...</Text>
        </View>
      </AppScreen>
    );
  }

  if (step === 'recognitionFailed') {
    return (
      <AppScreen contentStyle={styles.center}>
        <View style={[styles.card, themed.card]} accessibilityRole="alert">
          <Text style={themed.title}>Visitor check failed</Text>
          <Text style={themed.body}>{recognitionError}</Text>
          <Text style={themed.muted}>Please try Mark Attendance again.</Text>
          <AppButton title="Done" variant="text" onPress={exitNow} />
        </View>
      </AppScreen>
    );
  }

  if (step === 'hrContact') {
    const contact = hrContact.status === 'loaded' ? hrContact.contact : null;
    const hasDetails = Boolean(contact?.phoneNumber || contact?.email);
    return (
      <AppScreen contentStyle={styles.center}>
        <View style={[styles.card, themed.card]} accessibilityRole="summary" accessibilityLabel="HR contact">
          <Text style={themed.title}>Contact HR</Text>
          {hrContact.status === 'loading' ? (
            <ActivityIndicator size="large" color={palette.info} accessibilityLabel="Loading HR contact" />
          ) : hasDetails && contact ? (
            <View style={styles.contactList}>
              {contact.contactName ? <Text style={themed.body}>{contact.contactName}</Text> : null}
              {contact.phoneNumber ? (
                <View style={styles.contactRow}>
                  <Text style={themed.label}>Phone</Text>
                  <Text style={themed.value} accessibilityLabel={`HR phone ${contact.phoneNumber}`}>
                    {contact.phoneNumber}
                  </Text>
                </View>
              ) : null}
              {contact.email ? (
                <View style={styles.contactRow}>
                  <Text style={themed.label}>Email</Text>
                  <Text style={themed.value} accessibilityLabel={`HR email ${contact.email}`}>
                    {contact.email}
                  </Text>
                </View>
              ) : null}
            </View>
          ) : (
            <Text style={themed.body} accessibilityRole="alert">
              HR contact details are not available right now. Please ask at the reception desk.
            </Text>
          )}
          {hrContact.status === 'loading' ? null : (
            <Text style={themed.muted}>Returning to home shortly...</Text>
          )}
          <AppButton title="Done" variant="text" onPress={exitNow} />
        </View>
      </AppScreen>
    );
  }

  if (step === 'form') {
    const field = (
      key: keyof QuickVisitorFormValues,
      label: string,
      options: Partial<React.ComponentProps<typeof AppInput>> = {},
    ) => (
      <AppInput
        label={label}
        value={values[key]}
        onChangeText={value => setField(key, value)}
        error={errors[key]}
        editable={!submitting}
        {...options}
      />
    );
    return (
      <AppScreen scroll keyboardSafe contentStyle={styles.formContent}>
        <View style={[styles.card, styles.formCard, themed.card]}>
          <Text style={themed.title}>Visitor registration</Text>
          <Text style={themed.muted}>Fields marked * are required.</Text>
          {faceImage ? (
            <Text style={themed.muted}>
              The photo from your scan is saved with your registration so you are
              recognised when you check out.
            </Text>
          ) : null}
          {submitError ? (
            <View style={[styles.banner, themed.banner]} accessibilityRole="alert">
              <Text style={themed.error}>{submitError}</Text>
            </View>
          ) : null}
          {field('visitorName', 'Visitor name *', { placeholder: 'Full name', autoCapitalize: 'words' })}
          {field('designation', 'Designation', { autoCapitalize: 'words' })}
          {field('companyName', 'Company name', { autoCapitalize: 'words' })}
          {field('purposeOfVisit', 'Purpose of visit', { placeholder: 'e.g. Interview, vendor meeting' })}
          {field('phoneNumber', 'Phone number *', { keyboardType: 'phone-pad', placeholder: '+91 98765 43210' })}
          {field('email', 'Email address', {
            keyboardType: 'email-address',
            autoCapitalize: 'none',
            autoCorrect: false,
          })}
          <AppButton
            title="Register"
            onPress={() => {
              submit().catch(() => undefined);
            }}
            loading={submitting}
            disabled={submitting}
            style={styles.fullWidth}
          />
          <AppButton title="Cancel" variant="text" onPress={exitNow} disabled={submitting} />
        </View>
      </AppScreen>
    );
  }

  if (step === 'welcome') {
    return (
      <AppScreen contentStyle={styles.center}>
        <View style={[styles.card, themed.card]} accessibilityRole="summary" accessibilityLabel="Visitor registered">
          <View style={[styles.icon, { backgroundColor: palette.success }]}>
            <Text style={[styles.iconMark, { color: palette.onSuccess }]}>✓</Text>
          </View>
          <Text style={themed.title}>{welcomeMessage}</Text>
          <Text style={themed.body}>You are registered as a visitor.</Text>
          {registration?.checkedIn && registration.visit ? (
            <>
              <View style={styles.contactRow}>
                <Text style={themed.label}>Check In Time</Text>
                <Text style={themed.value}>{formatTime(registration.visit.checkInAt)}</Text>
              </View>
              <Text style={themed.body}>
                Scan your face at Mark Attendance when you leave to check out.
              </Text>
            </>
          ) : registration?.faceRegistered ? (
            <Text style={themed.body}>
              Your face is registered. Scan your face at Mark Attendance again to check in.
            </Text>
          ) : null}
          <Text style={themed.muted}>Returning to home shortly...</Text>
        </View>
      </AppScreen>
    );
  }

  return (
    <AppScreen contentStyle={styles.center}>
      <View style={[styles.card, themed.card]} accessibilityRole="alert" accessibilityLabel="Are you a visitor?">
        <Text style={themed.title}>Are you a visitor?</Text>
        <Text style={themed.body}>Your face did not match a registered employee.</Text>
        <AppButton title="Visitor" onPress={() => setStep('form')} style={styles.fullWidth} />
        <AppButton
          title="Contact HR"
          variant="secondary"
          onPress={() => {
            showHrContact().catch(() => undefined);
          }}
          style={styles.fullWidth}
        />
        <AppButton title="Cancel" variant="text" onPress={exitNow} />
      </View>
    </AppScreen>
  );
}

const styles = StyleSheet.create({
  center: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  formContent: {
    alignItems: 'center',
    paddingVertical: spacing.xl,
  },
  card: {
    width: '100%',
    maxWidth: 520,
    borderRadius: 20,
    borderWidth: 1,
    paddingVertical: spacing.xxl,
    paddingHorizontal: spacing.xl,
    gap: spacing.lg,
    alignItems: 'center',
  },
  formCard: {
    alignItems: 'stretch',
    paddingVertical: spacing.xl,
    gap: spacing.md,
  },
  fullWidth: { alignSelf: 'stretch', minHeight: 52 },
  banner: {
    borderWidth: 1,
    borderRadius: 8,
    padding: spacing.md,
  },
  contactList: { alignItems: 'center', gap: spacing.md },
  contactRow: { alignItems: 'center', gap: 2 },
  icon: {
    width: 72,
    height: 72,
    borderRadius: 36,
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconMark: { fontSize: 40, lineHeight: 44, fontWeight: '700' },
});
